import * as React from "react";
import { FieldProps } from "formik";
import { Form } from "antd";
import { useState } from "react";
 
const FormItem = Form.Item;
 
interface CountryCode {
  code: string;
  label: string;
}
 
export const PhoneInputField: React.FC<
  FieldProps<any> & {
    description?: string;
    prefix: React.ReactNode;
    label?: string;
    countries: CountryCode[];
    placeholder?: string;
    selectPosition?: "start" | "end";
    success?: boolean;
    hint?: string;
    className?: string;
    disabled?: boolean;
    autoFocus?: boolean;
  }
> = ({
  field: { onChange, ...field },
  form: { touched, errors, setFieldValue, setFieldTouched },
  label,
  countries,
  placeholder = "+1 (555) 000-0000",
  selectPosition = "start",
  success = false,
  hint,
  className = "",
  disabled = false,
  autoFocus = false,
  description,
  ...props
}) => {
    const [selectedCountry, setSelectedCountry] = useState<string>(countries[0]?.code || "");
    const [phoneNumber, setPhoneNumber] = useState<string>(() => {
      // Initialize with formatted country code on first render
      if (countries[0]?.label) {
        return countries[0].label;
      }
      return "";
    });
 
    const countryCodes: Record<string, string> = React.useMemo(() =>
      countries.reduce((acc, { code, label }) => ({ ...acc, [code]: label }), {}),
      [countries]
    );
 
    // Centralized error message
    const errorMsg =
      (touched[field.name] && errors[field.name]) ||
      (touched["phone"] && errors["phone"]);
 
    // Initialize phone number from field value when it changes externally
    React.useEffect(() => {
      if (field.value && field.value !== phoneNumber.replace(/\s/g, '')) {
        const currentPrefix = countryCodes[selectedCountry];
        const formattedValue = formatPhoneNumber(field.value, currentPrefix);
        setPhoneNumber(formattedValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value, selectedCountry, countryCodes]);
 
    // Format phone number with spaces
    const formatPhoneNumber = React.useCallback((value: string, countryPrefix: string) => {
      // Remove all spaces first to get clean number
      const cleanValue = value.replace(/\s/g, '');
      
      // If the value is just the prefix, return it as is
      if (cleanValue === countryPrefix) {
        return cleanValue;
      }
      
      // Get the number part after the country code
      const numberPart = cleanValue.substring(countryPrefix.length);
      
      // Format: country code + space + groups of 3 digits + last 4 digits
      let formatted = countryPrefix;
      
      if (numberPart.length > 0) {
        formatted += ' ';
        
        // Add groups of 3 digits with spaces
        let i = 0;
        while (i < numberPart.length) {
          // For the last group, if remaining digits are 4 or less, take them all
          if (numberPart.length - i <= 4) {
            formatted += numberPart.substring(i);
            break;
          } else {
            // Take 3 digits and add space
            formatted += numberPart.substring(i, i + 3);
            if (i + 3 < numberPart.length) {
              formatted += ' ';
            }
            i += 3;
          }
        }
      }
      
      return formatted;
    }, []);
 
    const handleCountryChange = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
      if (disabled) return;
      const newCountry = e.target.value;
      setSelectedCountry(newCountry);
      const newPrefix = countryCodes[newCountry];
      const formattedNumber = formatPhoneNumber(newPrefix, newPrefix);
      setPhoneNumber(formattedNumber);
      setFieldValue(field.name, newPrefix);
    }, [disabled, countryCodes, formatPhoneNumber, field.name, setFieldValue]);
 
    const handlePhoneNumberChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      const newValue = e.target.value;
      const currentPrefix = countryCodes[selectedCountry];
 
      // Remove spaces to check the actual number
      const cleanNewValue = newValue.replace(/\s/g, '');
      const cleanCurrentPrefix = currentPrefix.replace(/\s/g, '');
 
      // Prevent deletion of the country code prefix
      if (
        cleanNewValue.length < cleanCurrentPrefix.length ||
        !cleanNewValue.startsWith(cleanCurrentPrefix)
      ) {
        const formattedPrefix = formatPhoneNumber(currentPrefix, currentPrefix);
        setPhoneNumber(formattedPrefix);
        setFieldValue(field.name, cleanCurrentPrefix);
        return;
      }
 
      // Remove leading zero after prefix if present
      let processedValue = cleanNewValue;
      if (cleanNewValue.length > cleanCurrentPrefix.length) {
        const numberPart = cleanNewValue.substring(cleanCurrentPrefix.length);
        if (numberPart.startsWith('0')) {
          // Remove the leading zero from the number part
          const numberWithoutZero = numberPart.substring(1);
          processedValue = cleanCurrentPrefix + numberWithoutZero;
        }
      }
 
      // Format the new value and update state
      const formattedValue = formatPhoneNumber(processedValue, cleanCurrentPrefix);
      setPhoneNumber(formattedValue);
      setFieldValue(field.name, processedValue);
    }, [disabled, countryCodes, selectedCountry, formatPhoneNumber, field.name, setFieldValue]);
 
    const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      const currentPrefix = countryCodes[selectedCountry];
      const input = e.target as HTMLInputElement;
      const cursorPosition = input.selectionStart || 0;
      const phoneValue = input.value;
 
      // Find the actual prefix position (account for spaces)
      const cleanPrefix = currentPrefix.replace(/\s/g, '');
      const spaceCount = (phoneValue.substring(0, cleanPrefix.length + 1).match(/\s/g) || []).length;
      const prefixEndPosition = cleanPrefix.length + spaceCount;
 
      // Prevent backspace/delete if cursor is within the prefix
      if (
        (e.key === "Backspace" || e.key === "Delete") &&
        cursorPosition <= prefixEndPosition
      ) {
        e.preventDefault();
      }
    }, [countryCodes, selectedCountry]);
 
    const handleClick = React.useCallback((e: React.MouseEvent<HTMLInputElement>) => {
      const input = e.target as HTMLInputElement;
      const currentPrefix = countryCodes[selectedCountry];
 
      setTimeout(() => {
        // Find where the prefix ends in the formatted number
        const formattedPrefix = formatPhoneNumber(currentPrefix, currentPrefix);
        const prefixLength = formattedPrefix.length;
        
        if (
          input.selectionStart !== null &&
          input.selectionStart < prefixLength
        ) {
          input.setSelectionRange(prefixLength, prefixLength);
        }
      }, 0);
    }, [countryCodes, selectedCountry, formatPhoneNumber]);
 
    const handleBlur = React.useCallback(() => {
      setFieldTouched(field.name, true);
    }, [field.name, setFieldTouched]);
 
    const getInputClasses = () => {
      let classes = `h-[3rem] text-[#fff] w-full !rounded-lg !border-none !text-white appearance-none px-4 py-3 text-[14px] !bg-[#1E1E1E] text-[14px] backdrop-blur-sm text-white/90 placeholder:text-white/40 focus:outline-0 focus:border-[#40a9ff] focus:shadow-[0_0_0_2px_rgba(24,144,255,0.2)] shadow-lg  ${selectPosition === "start" ? "pl-[84px] pr-4" : "pl-4 pr-[84px]"}`
      if (disabled) {
        classes += " bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:border-gray-700";
      } else if (errorMsg) {
        classes += " border-red-500 text-red-600 focus:outline-0 focus:border-red-500 focus:shadow-[0_0_0_2px_rgba(239,68,68,0.2)] bg-red-50 dark:bg-red-950/20 dark:border-red-500 dark:text-red-400";
      }
 
      return classes;
    };
 
    return (
      <FormItem
        label={label}
        help={
          typeof errorMsg === "string"
            ? errorMsg
            : (hint && !errorMsg ? hint : undefined)
        }
        validateStatus={errorMsg ? "error" : undefined}
      >
        <div className="w-full">
          {label && <div className="text-white text-sm mb-2">{label}</div>}
          <div className={`relative flex flex-col ${className}`}>
            <div className="flex w-full">
              {/* Dropdown position: Start */}
              {selectPosition === "start" && (
                <div className="absolute z-10">
                  <select
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    disabled={disabled}
                    className="appearance-none h-[48px] rounded-l-lg border-0 border-r border-white/20 !bg-[#1E1E1E] py-3 pl-3.5 pr-8 text-[14px] leading-tight text-[#8E8E8E] focus:outline-none backdrop-blur-sm shadow-lg transition-all duration-200"
                  >
                    {countries.map((country) => (
                      <option
                        key={country.code}
                        value={country.code}
                        className="text-[#8E8E8E] !bg-[#1E1E1E]"
                      >
                        {country.code}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 flex items-center text-[#8E8E8E] pointer-events-none right-3">
                    <svg
                      className="stroke-current"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.79175 7.396L10.0001 12.6043L15.2084 7.396"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              )}
 
              {/* Input field */}
              <input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                onKeyDown={handleKeyDown}
                onClick={handleClick}
                placeholder={placeholder}
                onBlur={handleBlur}
                disabled={disabled}
                className={getInputClasses()}
              />
 
              {/* Dropdown position: End */}
              {selectPosition === "end" && (
                <div className="absolute right-0 z-10">
                  <select
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    disabled={disabled}
                    className="appearance-none h-[48px] rounded-r-lg text-[14px] border-0 border-l border-white/20 !bg-[#1E1E1E] py-3 pl-3.5 pr-8 leading-tight text-[#8E8E8E] focus:outline-none focus:ring-2 backdrop-blur-sm shadow-lg transition-all duration-200"
                  >
                    {countries.map((country) => (
                      <option
                        key={country.code}
                        value={country.code}
                        className="text-[#8E8E8E] !bg-[#1E1E1E]"
                      >
                        {country.code}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 flex items-center text-[#8E8E8E] pointer-events-none right-3">
                    <svg
                      className="stroke-current"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.79175 7.396L10.0001 12.6043L15.2084 7.396"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </FormItem>
    );
  };